// ============================================================================
// audio.js — WebAudio synth stubs. No samples; everything is oscillators + a
// noise buffer shaped by envelopes and filters. Lazily created on the first
// user gesture (the start button), then driven by game events.
// ============================================================================

import { EV } from '../core/events.js';

export class AudioEngine {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.noiseBuf = null;
    this._lastHit = 0;
    this.enabled = true;
  }

  start() {
    if (this.ctx) { if (this.ctx.state === 'suspended') this.ctx.resume(); return; }
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) { this.enabled = false; return; }
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = this._vol != null ? this._vol : 0.5;
    this.master.connect(this.ctx.destination);
    // 1s of white noise, reused
    const len = this.ctx.sampleRate;
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    this.noiseBuf = buf;
  }

  setVolume(v) { this._vol = v; if (this.master) this.master.gain.value = v; }

  _now() { return this.ctx.currentTime; }

  _tone(freq, dur, type, gain, slideTo) {
    if (!this.ctx) return;
    const t = this._now();
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type || 'sawtooth';
    o.frequency.setValueAtTime(freq, t);
    if (slideTo) o.frequency.exponentialRampToValueAtTime(Math.max(20, slideTo), t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g); g.connect(this.master);
    o.start(t); o.stop(t + dur + 0.02);
  }

  _noise(dur, gain, filterFreq, filterType) {
    if (!this.ctx) return;
    const t = this._now();
    const src = this.ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    const f = this.ctx.createBiquadFilter();
    f.type = filterType || 'bandpass';
    f.frequency.value = filterFreq || 1200;
    f.Q.value = 0.9;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(f); f.connect(g); g.connect(this.master);
    src.start(t); src.stop(t + dur + 0.02);
  }

  swing(heavy) { this._noise(heavy ? 0.22 : 0.14, heavy ? 0.25 : 0.16, heavy ? 900 : 1600, 'bandpass'); }

  hit(kill) {
    if (!this.ctx) return;
    const t = this._now();
    if (t - this._lastHit < 0.028) return;
    this._lastHit = t;
    this._noise(0.09, 0.3, 500, 'lowpass');
    this._tone(180 + Math.random() * 60, 0.1, 'square', 0.14, 70);
    if (kill) this._tone(90, 0.18, 'sawtooth', 0.12, 40);
  }

  playerHurt() { this._tone(120, 0.22, 'sawtooth', 0.24, 55); this._noise(0.12, 0.2, 380, 'lowpass'); }
  guard() { this._tone(880, 0.12, 'square', 0.16, 1400); this._noise(0.06, 0.14, 3200, 'highpass'); }
  pickup() { this._tone(520, 0.14, 'triangle', 0.16, 900); }

  horn() {
    this._tone(110, 0.9, 'sawtooth', 0.22, 130);
    this._tone(165, 0.9, 'sawtooth', 0.18, 190);
  }
  titan() {
    this._noise(1.2, 0.4, 220, 'lowpass');
    this._tone(60, 1.4, 'sawtooth', 0.35, 30);
    this._tone(90, 1.2, 'square', 0.2, 45);
  }
  sting(good) {
    this._tone(good ? 330 : 220, 0.5, 'sawtooth', 0.22, good ? 660 : 130);
    this._tone(good ? 494 : 175, 0.5, 'triangle', 0.16, good ? 740 : 110);
  }

  init(world) {
    const ev = world.events;
    ev.on(EV.SWING, ({ heavy }) => this.swing(heavy));
    ev.on(EV.HIT, ({ kill, blocked }) => { if (blocked) this.guard(); else this.hit(kill); });
    ev.on(EV.PLAYER_HURT, () => this.playerHurt());
    ev.on(EV.TITANFALL, () => this.titan());
    ev.on(EV.WARLORD_SLAIN, () => { this.sting(true); this.horn(); });
    ev.on(EV.WARLORD_ENGAGED, () => this.horn());
    ev.on(EV.BASTION_CAPTURED, ({ team }) => { this.horn(); this.sting(team === 0); });
    ev.on(EV.MILESTONE, () => this.sting(true));
    ev.on(EV.VICTORY, () => this.sting(true));
    ev.on(EV.DEFEAT, () => this.sting(false));
    // pickups: crowd_ai spawns heal dmgnum; hook via a simple event? reuse HIT? skip.
  }
}
